import React from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { AlertCircle, FileText, Clock, Mail, Phone, ExternalLink, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ComplaintsPage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-24 pb-16">
        <div className="section-container">
          {/* Header */}
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground">
                  Complaints Procedure
                </h1>
                <p className="text-muted-foreground">How to make a complaint about our services</p>
              </div>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                We are committed to providing excellent service. However, if something goes wrong, 
                we want to know about it so we can put things right. This page explains how to make 
                a complaint and what you can expect.
              </p>

              {/* Our Commitment */}
              <section className="mb-10 p-6 rounded-xl bg-accent/10 border border-accent/30">
                <h2 className="font-heading text-xl font-bold text-foreground mb-4">
                  Our Commitment to You
                </h2>
                <p className="text-foreground/80">
                  As a firm regulated by the Solicitors Regulation Authority (SRA), we take all complaints seriously. 
                  We will investigate your complaint thoroughly and respond promptly.
                </p>
              </section>

              {/* How to Complain */}
              <section className="mb-10">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="w-5 h-5 text-accent" />
                  <h2 className="font-heading text-xl font-bold text-foreground m-0">
                    How to Make a Complaint
                  </h2>
                </div>
                <div className="pl-8 space-y-4 text-foreground/80">
                  <p>You can make a complaint by:</p>
                  
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <div className="p-4 rounded-lg bg-muted/50 border border-border">
                      <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                        <Mail className="w-4 h-4 text-accent" />
                        Email
                      </h3>
                      <a href="mailto:complaints@bluelionclaims.co.uk" className="text-accent hover:underline">
                        complaints@bluelionclaims.co.uk
                      </a>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50 border border-border">
                      <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                        <Phone className="w-4 h-4 text-accent" />
                        Phone
                      </h3>
                      <a href="tel:02081530033" className="text-accent hover:underline">
                        0208 153 0033
                      </a>
                    </div>
                  </div>

                  <div className="p-4 rounded-lg bg-muted/50 border border-border mt-4">
                    <h3 className="font-semibold text-foreground mb-2">Post</h3>
                    <p>
                      Complaints Team<br />
                      Blue Lion Law Limited<br />
                      The Centro Buildings<br />
                      20-23 Mandela Street<br />
                      London, NW1 0DU
                    </p>
                  </div>
                </div>
              </section>

              {/* What to Include */}
              <section className="mb-10">
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle className="w-5 h-5 text-accent" />
                  <h2 className="font-heading text-xl font-bold text-foreground m-0">
                    What to Include in Your Complaint
                  </h2>
                </div>
                <div className="pl-8 space-y-4 text-foreground/80">
                  <p>To help us investigate, please include:</p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>Your full name and contact details</li>
                    <li>Your reference number (if you have one)</li>
                    <li>A clear description of what went wrong</li>
                    <li>When the problem occurred</li>
                    <li>What you would like us to do to resolve it</li>
                    <li>Any relevant documents or correspondence</li>
                  </ul>
                </div>
              </section>

              {/* Timeline */}
              <section className="mb-10">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="w-5 h-5 text-accent" />
                  <h2 className="font-heading text-xl font-bold text-foreground m-0">
                    What Happens Next
                  </h2>
                </div>
                <div className="pl-8 space-y-4 text-foreground/80">
                  <div className="space-y-4">
                    <div className="flex gap-4 items-start">
                      <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm flex-shrink-0">
                        1
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground">Acknowledgement</h3>
                        <p>We will acknowledge your complaint within 3 working days.</p>
                      </div>
                    </div>
                    <div className="flex gap-4 items-start">
                      <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm flex-shrink-0">
                        2
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground">Investigation</h3>
                        <p>We will investigate your complaint thoroughly, which may involve reviewing your file and speaking with relevant staff.</p>
                      </div>
                    </div>
                    <div className="flex gap-4 items-start">
                      <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm flex-shrink-0">
                        3
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground">Response</h3>
                        <p>We aim to provide a full written response within 8 weeks of receiving your complaint.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* If Not Satisfied */}
              <section className="mb-10">
                <div className="flex items-center gap-3 mb-4">
                  <ExternalLink className="w-5 h-5 text-accent" />
                  <h2 className="font-heading text-xl font-bold text-foreground m-0">
                    If You Are Not Satisfied
                  </h2>
                </div>
                <div className="pl-8 space-y-4 text-foreground/80">
                  <p>
                    If you are not satisfied with our response, or if we have not resolved your complaint within 
                    8 weeks, you may refer your complaint to the Legal Ombudsman.
                  </p>
                  
                  <div className="p-4 rounded-lg bg-muted/50 border border-border mt-4">
                    <h3 className="font-semibold text-foreground mb-3">Legal Ombudsman</h3>
                    <p className="mb-2">
                      The Legal Ombudsman investigates complaints about solicitors and other legal service providers.
                    </p>
                    <ul className="space-y-2">
                      <li><strong>Website:</strong> <a href="https://www.legalombudsman.org.uk" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">www.legalombudsman.org.uk</a></li>
                      <li><strong>Email:</strong> enquiries@legalombudsman.org.uk</li>
                      <li><strong>Phone:</strong> 0300 555 0333</li>
                      <li><strong>Post:</strong> Legal Ombudsman, PO Box 6167, Slough, SL1 0EH</li>
                    </ul>
                    <p className="mt-4 text-sm">
                      <strong>Time limit:</strong> Normally, you must bring your complaint to the Legal Ombudsman within 
                      6 months of our final response to your complaint.
                    </p>
                  </div>

                  <div className="p-4 rounded-lg bg-muted/50 border border-border mt-4">
                    <h3 className="font-semibold text-foreground mb-3">Solicitors Regulation Authority (SRA)</h3>
                    <p className="mb-2">
                      If you believe we have breached the SRA's rules or principles, you can report us to the SRA. 
                      However, the SRA is not able to award compensation or deal with complaints about poor service.
                    </p>
                    <p>
                      <strong>Website:</strong> <a href="https://www.sra.org.uk" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">www.sra.org.uk</a>
                    </p>
                  </div>
                </div>
              </section>

              {/* Contact */}
              <section className="mt-12 p-6 rounded-xl bg-muted/50 border border-border">
                <h2 className="font-heading text-xl font-bold text-foreground mb-4">
                  Contact Our Complaints Team
                </h2>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-accent" />
                    <a href="mailto:complaints@bluelionclaims.co.uk" className="text-accent hover:underline">
                      complaints@bluelionclaims.co.uk
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-accent" />
                    <a href="tel:02081530033" className="text-accent hover:underline">
                      0208 153 0033
                    </a>
                  </div>
                </div>
                <Button variant="outline" asChild>
                  <a href="mailto:complaints@bluelionclaims.co.uk">
                    Submit a Complaint
                  </a>
                </Button>
              </section>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ComplaintsPage;
